import * as React from 'react';
import {Breadcrumb, Grid} from 'semantic-ui-react';
import i18n from '../../../../locale/i18n';
import {NavLink, RouteComponentProps, withRouter} from 'react-router-dom';
import {
  ColumnSelectableComponentProps,
  ColumnSortableComponentProps,
  FilterObjectComponentProps,
  InfinityScrollComponentProps,
  ViewSettingsComponentProps,
  withColumnSelection,
  withColumnSort,
  withFilterObject,
  withInfinityScrollHandlers,
  withPrintAndExport,
  withViewSettings
} from '../../components/DataTable/dataService';
import {DataTable} from '../../components/DataTable/DataTable';
import LoadingSpinner from '../../containers/LoadingContainer/LoadingSpinner';
import {ColumnDefinition} from '../../components/DataTable/common';
import ContentContainer from '../../containers/ContentContainer/ContentContainer';
import {ColumnSelectionButton} from '../../components/DataTable/ColumnSelection';
import LoadingContainer from '../../containers/LoadingContainer/LoadingContainer';
import _get from 'lodash/get';
import Moment from 'react-moment';
import moment from 'moment';
import LogisticsService from '../../services/LogisticsService';
import {AnalyticsTracking} from "../../components/AnalyticsTracking/AnalyticsTracking";

const t = i18n.t.bind(i18n);


const COLUMNS: ColumnDefinition[] = [
  {labelTranslationKey: 'Inventory.LTNumber', fieldName: 'data.ltNummer'}, // by default only print/export
  {labelTranslationKey: 'Inventory.ShortText', fieldName: 'data.kurztextLT'}, // by default only print/export
  {labelTranslationKey: 'InventoryAccountType.Market', fieldName: 'data.kurzname2'}, // by default only print/export
  {
    labelTranslationKey: "MovementTypeAccountLocation.Movement", fieldName: "data.datumBewegung",
    cellRender: (v, r) => <Moment format={t("Common.DateFormat")}>{v}</Moment>,
    printExportRender: (v, r) => moment(v).format(t("Common.DateFormat"))
  },
  {
    labelTranslationKey: "MovementTypeAccountLocation.Entry", fieldName: "data.datumBuchung", required: true,
    cellRender: (v, r) => <Moment
        format={t("Common.DateFormat")}>{r.data.datumBuchung + 'T' + r.data.zeitBuchung}</Moment>,
    printExportRender: (v, r) => moment(r.data.datumBuchung + 'T' + r.data.zeitBuchung).format(t("Common.DateFormat"))
  },
  {
    labelTranslationKey: "MovementTypeAccountLocation.Time", fieldName: "data.zeitBuchung", required: true,
    cellRender: (v, r) => <Moment
        format={t("Common.TimeFormat")}>{r.data.datumBuchung + 'T' + r.data.zeitBuchung}</Moment>,
    printExportRender: (v, r) => moment(r.data.datumBuchung + 'T' + r.data.zeitBuchung).format(t("Common.TimeFormat"))
  },
  {
    labelTranslationKey: "MovementTypeAccountLocation.Count1",
    fieldName: "data.anzahl1",
    cellAlign: 'right'
  },
  {
    labelTranslationKey: "MovementTypeAccountLocation.Count2",
    fieldName: "data.anzahl2",
    cellAlign: 'right'
  },
  {
    labelTranslationKey: "MovementTypeAccountLocation.Count3",
    fieldName: "data.anzahl3",
    cellAlign: 'right'
  },
  {
    labelTranslationKey: "MovementTypeAccountLocation.Count4",
    fieldName: "data.anzahl4",
    cellAlign: 'right'
  },
  {labelTranslationKey: "MovementTypeAccountLocation.EBNr", fieldName: "data.ebNr"},
  {labelTranslationKey: "MovementTypeAccountLocation.Shortname", fieldName: "data.kurzname"},
  {labelTranslationKey: "MovementTypeAccountLocation.BUSL", fieldName: "data.BUSL"},
  {labelTranslationKey: "MovementTypeAccountLocation.BUSLText", fieldName: "data.text01"},
  {labelTranslationKey: "MovementTypeAccountLocation.ShortText2", fieldName: "data.kurztextBT"}
];

const getScrollParent = () => ContentContainer.instance.componentRef.current;

// filter out print/export columns in default columns
const DEFAULT_COLUMNS = COLUMNS.map(c => c.fieldName).filter(f => (f !== 'data.kurznameFakd' && f !== 'data.kurztextLT' && f !== 'data.kurzname2' && f !== 'data.ltNummer'));

const PageComponent = (props: RouteComponentProps<{ accountNumber: string; ltNumber: string; market: string; }> & InfinityScrollComponentProps & ColumnSelectableComponentProps & ColumnSortableComponentProps & FilterObjectComponentProps & ViewSettingsComponentProps) => {
  if (props.loadingInitial) {
    return <LoadingContainer showLogo={false}/>;
  }
  const getAccountName = () => {
    if (props.data != null) {
      return _get(props.data, '0.data.kurznameFakd', '');
    }
    return '';
  };

  const getLtName = () => {
    if (props.data != null) {
      return _get(props.data, '0.data.kurztextLT', '');
    }
    return '';
  };

  const getMarketName = () => {
    if (props.data != null) {
      return _get(props.data, '0.data.kurzname2', '');
    }
    return '';
  };

  return (
      <>
        <AnalyticsTracking page="logistics/account/market" />
        <div className={"sticky-header"}>
          <div className={"ui container"}>
            <h3><Breadcrumb icon="right angle" sections={[
              {
                key: '1',
                as: NavLink,
                to: '/logistics/inventory',
                content: `${t("Route.Inventory")}`, link: true
              },
              {
                key: '2',
                content: `${t("Inventory.Account")}: ${props.match.params.accountNumber} ${getAccountName()}`,
                as: NavLink,
                to: `/logistics/inventory/${props.match.params.accountNumber}/${props.match.params.ltNumber}`,
                active: false
              },
              {
                key: '3',
                content: `${t("Inventory.LTNumber")}: ${props.match.params.ltNumber} ${getLtName()}`,
                as: NavLink,
                to: `/logistics/inventory/${props.match.params.accountNumber}/${props.match.params.ltNumber}`,
                active: false
              },
              {
                key: '4',
                content: `${t("InventoryAccountType.Market")}: ${props.match.params.market} ${getMarketName()}`,
                active: true
              },
            ]}/></h3>
            <p>{t("Common.Found$dDatasets", {count: props.totalItems})}</p> <br/>
            <Grid columns={3} className={"HeadingGrid"} stackable={true}
                  style={{marginBottom: '1em'}}>
              <Grid.Column width={11}>
              </Grid.Column>
              <Grid.Column width={4}>
                <ColumnSelectionButton
                    fluid
                    availableColumns={COLUMNS}
                    selectedColumns={props.selectedColumns}
                    onSave={props.updateSelectedColumns}/>
              </Grid.Column>
              <Grid.Column width={1}>
                <div style={{opacity: (props.loading) ? '1.0' : '0.0', width:"100%"}}>
                  <div style={{textAlign: 'center', height: '3em', marginTop: '1.5em'}}><LoadingSpinner/>
                  </div>
                </div>
              </Grid.Column>
            </Grid>
          </div>
        </div>


        <div className={"ui container responsivewrapper"}>
          <DataTable
              columns={COLUMNS}
              data={props.data}
              page={props.page}
              totalPages={props.totalPages}
              sortDirection={props.sortDirection}
              sortColumn={props.sortColumn}
              selectedColumns={props.selectedColumns}
              getScrollParent={getScrollParent}
              loadMore={props.loadMore}
              handleSortChange={props.handleSortChange}
              loading={props.loading}
          />
          <div style={{opacity: props.loadingMore ? '1.0' : '0.0'}}>
            <div style={{textAlign: 'center', height: '3em', marginTop: '1.5em'}}><LoadingSpinner/>
            </div>
          </div>
          <div style={{height: '2em'}}/>
        </div>
      </>
  )
};

const WrappedComponent = withRouter(withViewSettings(withFilterObject(withColumnSort(withColumnSelection(withPrintAndExport(withInfinityScrollHandlers(PageComponent)))))));

export default (props: RouteComponentProps<{ accountNumber: string; ltNumber: string; market: string; }>) => {
  const dataService = {
    loadPage(page: number, sortColumn: string, sortOrder: 'ASC' | 'DESC', filter: { [p: string]: any }, size?: number): Promise<any> {
      return LogisticsService.getMovementDataForAccountAndLTNumberAndMarket(props.match.params.accountNumber, props.match.params.ltNumber, props.match.params.market,
          sortColumn, sortOrder, page, size || 50);
    }
  };
  return (
      <WrappedComponent
          viewName="inventory_movement"
          dataService={dataService}
          defaultSortDirection="DESC"
          defaultSortColumn="data.datumBewegung"
          defaultColumns={DEFAULT_COLUMNS}
          defaultFilter={{}}
          printColumns={COLUMNS}
          printPath={[
            `${t("Route.Inventory")}`,
            `${t("Inventory.Account")}: ${props.match.params.accountNumber}; ${t("Inventory.LTNumber")}: ${props.match.params.ltNumber}` // TODO add account Name and lt Name?!
          ]}
      />
  );

}
